import tw from 'twin.macro'
import React, { useEffect, useRef } from 'react'
import { CgInfo } from 'react-icons/cg'

import { useListenProductImgQuery, useUpdateOneTimeProductMutation } from 'modules/ProductInfo/productInfoApi'
import { useListenPublishedCheckoutPageQuery } from 'modules/PageBuilder/pageBuilderApi'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'

import { CardRender } from 'modules/Layout/components/Row/LayoutRow'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { AddItemButton } from 'common/components/AddItemButton/AddItemButton'
import { availableItemTypes } from 'common/components/AddItemButton/constants/constants'
import { useEventListener } from 'common/hooks/useEventListener'
import ContentFormatInput from './ContentFormatInput'
import Rows from './Rows'

export const inputRefsSortMethod = ['contentFormat', 'tabName']

function ContentForm({ coachOrgId, item, productId }) {
  const [updateOneTimeProduct] = useUpdateOneTimeProductMutation()

  const { data: productImg } = useListenProductImgQuery({ coachOrgId, id: productId })
  const productImgLoading = productImg === undefined || productImg?.isLoading
  const { data: publishedCheckoutPage } = useListenPublishedCheckoutPageQuery({ coachOrgId, productId })
  const publishedCheckoutPageLoading = publishedCheckoutPage === undefined || publishedCheckoutPage?.isLoading
  const isContentChangeDisabled = publishedCheckoutPageLoading || Boolean(publishedCheckoutPage)

  const { addInputRef, moveFocusOnKeyPress } = useFormRefsControl()
  const contentFormatInputRef = useRef()

  useEffect(() => {
    addInputRef({ ref: contentFormatInputRef, name: 'contentFormat', sortMethod: inputRefsSortMethod })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hideItems = Object.values(availableItemTypes).filter((item) => {
    if (item === availableItemTypes.PROGRAM && item?.type === availableItemTypes.WORKOUT) {
      return false
    } else if (item !== item?.type) {
      return true
    } else {
      return false
    }
  })

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e))
  const contentFormatExplanation = getContentFormatExplanation(item?.type)
  return (
    <>
      <div className='mt-12 bg-white rounded-xl border-2 border-gray-200'>
        <div className='pb-6 px-8 pt-8 max-w-2xl'>
          <div className='flex items-center mb-1'>
            <Label htmlFor='contentFormat'>Content Format</Label>
            {Boolean(contentFormatExplanation) && (
              <div className='mx-1 cursor-pointer text-gray-500 hover:text-tGreen'>
                <Tooltip
                  content={
                    <div className='flex flex-col'>
                      {contentFormatExplanation
                        .split('.')
                        .filter(Boolean)
                        .map((text, index) => (
                          <span key={index} className={index > 0 ? 'mb-1' : ''}>
                            {text}.
                          </span>
                        ))}
                    </div>
                  }
                >
                  <CgInfo className='w-4 h-4' />
                </Tooltip>
              </div>
            )}
          </div>
          <ContentFormatInput
            inputRef={contentFormatInputRef}
            item={item}
            coachOrgId={coachOrgId}
            productId={productId}
            disabled={isContentChangeDisabled}
          />
          {Boolean(item?.type) && !isContentChangeDisabled && (
            <p className='mt-1 text-xs text-gray-500 italic'>
              Note: content format will be locked after the checkout page is published.
            </p>
          )}
        </div>
        {Boolean(item?.type) &&
          (item?.type === 'tab' ? (
            <Rows tabId={item?.id} coachOrgId={coachOrgId} />
          ) : (
            <div className='pb-8 px-8'>
              <Label htmlFor='content' tw='mb-1 capitalize'>
                Select {item.type}
              </Label>
              {Boolean(item?.id) ? (
                <CardRender
                  item={item}
                  previewImg={productImgLoading ? null : productImg}
                  handleDelete={() =>
                    updateOneTimeProduct({
                      coachOrgId,
                      product: { item: { type: item.type, id: null, programId: null } },
                      productId,
                    })
                  }
                />
              ) : (
                <div
                  className='relative flex w-60 h-44 rounded-xl overflow-hidden items-center justify-center bg-offWhite'
                  data-testid='add-card'
                >
                  <AddItemButton
                    coachOrgId={coachOrgId}
                    handleAdd={(item) =>
                      updateOneTimeProduct({
                        coachOrgId,
                        product: { item },
                        productId,
                      })
                    }
                    hideItems={hideItems}
                    defaultCurrentItem={getDefaultCurrentItem(item?.type)}
                    disabledItems={getDisabledItems(item?.type)}
                  />
                </div>
              )}
            </div>
          ))}
      </div>
    </>
  )
}

export default ContentForm

function getContentFormatExplanation(type) {
  if (type === 'tab') {
    return 'After purchase, users will gain access to an extra tab in the Explore page of the app. The tab will showcase the rows below and the tab name will match the product name.'
  } else if (type === 'collection') {
    return 'After purchase, users will gain access to this collection.'
  } else if (type === 'program') {
    return 'After purchase, users will gain access to this program.'
  } else if (type === 'workout') {
    return 'After purchase, users will gain access to this workout.'
  } else if (type === 'video') {
    return 'After purchase, users will gain access to this video.'
  } else {
    return null
  }
}

function getDefaultCurrentItem(type) {
  if (type === availableItemTypes.PROGRAM || type === availableItemTypes.WORKOUT) {
    return { type: availableItemTypes.PROGRAM, id: null }
  } else {
    return { type, id: null }
  }
}

function getDisabledItems(type) {
  if (type === availableItemTypes.PROGRAM) {
    return { [availableItemTypes.WORKOUT]: true }
  } else if (type === availableItemTypes.WORKOUT) {
    return { [availableItemTypes.PROGRAM]: true }
  } else {
    return undefined
  }
}

const Label = tw.label`flex cursor-pointer font-semibold text-tBlack`
